"use client";

import { useEffect, useState } from "react";
import { LABEL_CLS } from "./theme";
import { useEditorialTheme } from "./theme-context";

/**
 * Bare row of palette swatches (no positioning/chrome). Reused by the
 * floating ThemeSwitcher (one-pagers) and inline inside the marketing nav
 * (home), so the picker stays a single source of truth.
 */
export function ThemeSwatches({ size = "md" }: { size?: "sm" | "md" }) {
  const { theme, setTheme, themes } = useEditorialTheme();
  const dim = size === "sm" ? "h-5 w-5" : "h-5 w-5 sm:h-6 sm:w-6";
  const dot = size === "sm" ? "h-1.5 w-1.5" : "h-1.5 w-1.5 sm:h-2 sm:w-2";

  return (
    <div className="flex items-center gap-1">
      {themes.map((t) => {
        const isActive = t.id === theme.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => setTheme(t)}
            aria-pressed={isActive}
            aria-label={`Switch to ${t.name} theme`}
            title={t.name}
            className={`relative grid ${dim} place-items-center rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40`}
            style={{
              backgroundColor: t.dark,
              outline: isActive ? "2px solid rgba(0,0,0,0.75)" : "none",
              outlineOffset: 1,
            }}
          >
            <span
              className={`${dot} rounded-full`}
              style={{ backgroundColor: t.accent }}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Hidden-by-default visibility for the palette picker.
 *
 * The picker is an internal tool, not an end-user control, so it stays
 * hidden until someone presses the "t" hotkey (each press toggles it).
 * Plain "t" only — ignored while a modifier is held or a form field has
 * focus. State is per-mount, so every fresh page load starts hidden.
 * Pass `enabled = false` to skip the listener where no picker is hosted.
 */
export function useThemePickerHotkey(enabled = true): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "t" && e.key !== "T") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (
        el &&
        (el.isContentEditable ||
          el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.tagName === "SELECT")
      ) {
        return;
      }
      setVisible((v) => !v);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);

  return enabled && visible;
}

/**
 * Floating palette picker — fixed top-right pill. Used on the
 * self-contained one-pagers (/one, /audere) where there's no global nav.
 * On pages with a nav, host ThemeSwatches inside the nav instead.
 *
 * Hidden until the "t" hotkey reveals it (see useThemePickerHotkey).
 */
export function ThemeSwitcher() {
  const visible = useThemePickerHotkey();
  if (!visible) return null;

  return (
    <div
      className="fixed right-4 top-4 z-50 flex items-center gap-1 rounded-full border bg-white/85 p-1.5 shadow-sm backdrop-blur"
      style={{ borderColor: "rgba(0,0,0,0.08)" }}
      aria-label="Theme switcher"
    >
      <span className={`ml-2 mr-1 hidden text-black/55 sm:inline ${LABEL_CLS}`}>
        Theme
      </span>
      <ThemeSwatches />
    </div>
  );
}
